import React, {
  useState, useEffect, useMemo, useRef, useCallback, memo,
} from "react";
import {
  Box, Typography, FormControl, Select, MenuItem,
  CircularProgress, TextField, InputAdornment,
  Chip, Avatar, Stack, Divider, Button, LinearProgress,
  Dialog, DialogContent, IconButton, Tooltip, Skeleton, Card, CardContent,
} from "@mui/material";
import ScienceIcon from "@mui/icons-material/Science";
import LocalGasStationIcon from "@mui/icons-material/LocalGasStation";
import WaterDropIcon from "@mui/icons-material/WaterDrop";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import LayersIcon from "@mui/icons-material/Layers";
import TerrainIcon from "@mui/icons-material/Terrain";
import SpeedIcon from "@mui/icons-material/Speed";
import TuneIcon from "@mui/icons-material/Tune";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RefreshIcon from "@mui/icons-material/Refresh";
import DescriptionIcon from "@mui/icons-material/Description";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import CloseIcon from "@mui/icons-material/Close";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import axios from "axios";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Document, Page, pdfjs } from "react-pdf";
import { useTranslation } from "react-i18next";

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

const D = {
  navy: "#1a2744",
  navyMid: "#243352",
  blue: "#1d4ed8",
  blueMid: "#2563eb",
  blueLight: "#eff6ff",
  blueBorder: "#bfdbfe",

  canvas: "#f1f4f9",
  surface: "#ffffff",
  surfaceAlt: "#f8fafc",
  surfaceHover: "#f0f4ff",

  text: "#0f172a",
  textMid: "#334155",
  textSoft: "#64748b",
  textXSoft: "#94a3b8",

  border: "#e2e8f0",
  borderMid: "#cbd5e1",
  borderFocus: "#93c5fd",

  green: "#15803d",
  greenLight: "#dcfce7",
  greenBorder: "#86efac",
  orange: "#c2410c",
  orangeLight: "#fff7ed",
  orangeBorder: "#fed7aa",
  purple: "#6d28d9",
  purpleLight: "#f5f3ff",
  purpleBorder: "#ddd6fe",
  red: "#b91c1c",
  amber: "#b45309",
  amberLight: "#fffbeb",
  amberBorder: "#fde68a",

  cyan: "#22d3ee",
  cyanDark: "#0891b2",
  cyanText: "#083344",

  shadow: "0 1px 3px rgba(15,23,42,0.08), 0 1px 2px rgba(15,23,42,0.06)",
  shadowMd: "0 4px 16px rgba(15,23,42,0.08)",
  shadowLg: "0 8px 32px rgba(15,23,42,0.10)",

  radius: "12px",
  radiusSm: "8px",
  radiusXs: "6px",
};

const LITO_COLOR = (lito = "") => {
  const l = lito.toLowerCase();
  if (l.includes("cobertura") || l.includes("cuaternari")) return { fill: "#d4a96a", stripe: "#c49252" };
  if (l.includes("meteoriz")) return { fill: "#cd8d60", stripe: "#b87650" };
  if (l.includes("granodiorita") || l.includes("granito")) return { fill: "#a89ec0", stripe: "#9288b0" };
  if (l.includes("pórfido") || l.includes("porfido")) return { fill: "#7ea872", stripe: "#6a9460" };
  if (l.includes("brecha")) return { fill: "#b87460", stripe: "#a05e4c" };
  if (l.includes("esquisto") || l.includes("gneis")) return { fill: "#82a0ae", stripe: "#6c8c9c" };
  return { fill: "#8fa0b2", stripe: "#7a8ea2" };
};

const ALTER_COLOR = (alt = "") => {
  const a = alt.toLowerCase();
  if (a.includes("propilítica") || a.includes("propilitica")) return { bg: D.greenLight, border: D.greenBorder, text: D.green, dot: "#22c55e" };
  if (a.includes("potásica") || a.includes("potasica")) return { bg: D.orangeLight, border: D.orangeBorder, text: D.orange, dot: "#f97316" };
  if (a.includes("argílica") || a.includes("arcilica") || a.includes("argilica")) return { bg: "#fef2f2", border: "#fecaca", text: D.red, dot: "#ef4444" };
  if (a.includes("oxidación") || a.includes("oxidacion")) return { bg: "#fef2f2", border: "#fecaca", text: D.red, dot: "#f87171" };
  if (a.includes("silicificación") || a.includes("silicif")) return { bg: D.blueLight, border: D.blueBorder, text: D.blue, dot: "#60a5fa" };
  return { bg: "#f8fafc", border: D.border, text: D.textSoft, dot: "#94a3b8" };
};

const initials = (name = "") =>
  name.split(" ").slice(0, 2).map((n) => n[0]).join("").toUpperCase();

const titleCase = (str = "") =>
  str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

const parseFechaToTimestamp = (f = "") => {
  if (!f) return 0;
  if (f instanceof Date) return f.getTime();
  const str = String(f).trim();
  if (str.includes("/")) {
    const parts = str.split("/");
    if (parts.length === 3) {
      const [d, m, y] = parts;
      const date = new Date(+y, +m - 1, +d);
      if (!isNaN(date.getTime())) return date.getTime();
    }
  } else if (str.includes("-")) {
    const cleanStr = str.split("T")[0].split(" ")[0];
    const parts = cleanStr.split("-");
    if (parts.length === 3) {
      const [y, m, d] = parts;
      const date = new Date(+y, +m - 1, +d);
      if (!isNaN(date.getTime())) return date.getTime();
    }
  }
  const t = Date.parse(str);
  return isNaN(t) ? 0 : t;
};

const formatFecha = (f = "") => {
  if (!f) return "";
  try {
    let d, m, y;
    const str = String(f).trim();
    if (str.includes("/")) {
      [d, m, y] = str.split("/");
    } else {
      const cleanStr = str.split("T")[0].split(" ")[0];
      [y, m, d] = cleanStr.split("-");
    }

    const months = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
    const days = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
    const date = new Date(+y, +m - 1, +d);
    if (isNaN(date.getTime())) return f;

    return `${days[date.getDay()]}, ${+d} de ${months[+m - 1]} de ${y}`;
  } catch { return f; }
};

const actividadColor = (act = "") => {
  const a = act.toLowerCase();
  if (a.includes("perforaci")) return D.blue;
  if (a.includes("stand") || a.includes("climat")) return D.orange;
  if (a.includes("almuerzo") || a.includes("comida")) return D.textSoft;
  if (a.includes("acondicion")) return "#0369a1";
  if (a.includes("maniobra") || a.includes("tuber")) return D.purple;
  return "#475569";
};

const rangeKey = (r) => r?._id || r?.parteId || `${r?.prof_inicial}_${r?.prof_final}_${r?.fecha}`;

const ANALYSIS_SECTIONS = [
  "Resumen general",
  "Litología",
  "Mineralización",
  "Alteraciones",
  "Estructuras, texturas u observaciones relevantes",
  "Limitaciones del dato",
];

const parseAnalysisText = (text = "") => {
  const lines = text.split("\n");
  const sections = [];
  let current = null;
  let tramo = "";

  lines.forEach((rawLine) => {
    const line = rawLine.trim();
    if (!line) return;

    if (line.toLowerCase().startsWith("tramo analizado")) {
      tramo = line.split(":").slice(1).join(":").trim();
      return;
    }

    const headerMatch = ANALYSIS_SECTIONS.find((h) => {
      const lower = line.toLowerCase();
      return lower === h.toLowerCase() || lower.startsWith(`${h.toLowerCase()}:`);
    });

    if (headerMatch) {
      current = {
        title: headerMatch,
        content: line.includes(":") ? line.split(":").slice(1).join(":").trim() : "",
      };
      sections.push(current);
      return;
    }

    if (current) {
      current.content = current.content ? `${current.content} ${line}` : line;
    }
  });

  return { tramo, sections };
};

const HeaderStat = memo(({ label, value, sub, unit }) => (
  <Card sx={{
    flex: "1 1 180px",
    background: "#0f0f47",
    color: 'white',
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 3,
    boxShadow: `0 8px 32px rgba(15,15,71,0.3)`,
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-8px)',
      boxShadow: `0 16px 48px rgba(15,15,71,0.4)`,
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: -20,
      right: -20,
      width: 120,
      height: 120,
      borderRadius: '50%',
      background: 'rgba(255,255,255,0.1)',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: -30,
      left: -30,
      width: 80,
      height: 80,
      borderRadius: '50%',
      background: 'rgba(255,255,255,0.05)',
    },
  }}>
    <CardContent sx={{ p: "24px !important", position: 'relative', zIndex: 1, height: '100%' }}>
      <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.7)", textTransform: "uppercase", letterSpacing: 1, mb: 1, fontWeight: 600 }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
        <Typography sx={{ fontSize: 36, fontWeight: 800, color: "#fff", lineHeight: 1 }}>
          {value}
        </Typography>
        {unit && <Typography sx={{ fontSize: 14, color: "rgba(255,255,255,0.6)", fontWeight: 500 }}>{unit}</Typography>}
      </Box>
      {sub && (
        <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.5)", mt: 1.5, lineHeight: 1.4 }}>{sub}</Typography>
      )}
    </CardContent>
  </Card>
));

const ActivityBar = memo(({ actividad, inicio, fin, total, cargo }) => {
  const hrs = parseFloat(String(cargo || "0").replace(",", ".")) || 0;
  const pct = Math.min(100, Math.round((hrs / 12) * 100));
  const color = actividadColor(actividad);
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.75, flexWrap: "wrap", gap: 0.5 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: D.text }}>{actividad}</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: 12, color: D.textSoft, fontVariantNumeric: "tabular-nums" }}>
            {inicio?.slice(0, 5)} – {fin?.slice(0, 5)}
          </Typography>
          <Box sx={{ px: 1.25, py: 0.25, borderRadius: "12px", bgcolor: `${color}1A`, border: `1px solid ${color}33`, color: color, fontSize: 11.5, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
            {total?.slice(0, 5)} h
          </Box>
        </Box>
      </Box>
      <LinearProgress variant="determinate" value={pct} sx={{
        height: 5, borderRadius: 3, bgcolor: D.border,
        "& .MuiLinearProgress-bar": { bgcolor: color, borderRadius: 3 },
      }} />
    </Box>
  );
});

const TeamMember = memo(({ rol, nombre, color, colorLight }) => (
  <Box sx={{
    flex: "1 1 200px",
    display: "flex", alignItems: "center", gap: 1.75,
    p: "16px 18px",
    borderRadius: D.radiusSm,
    border: `1px solid ${D.border}`,
    bgcolor: D.surfaceAlt,
    transition: "border-color 0.15s, background 0.15s",
    "&:hover": { bgcolor: D.surfaceHover },
  }}>
    <Avatar sx={{ width: 46, height: 46, bgcolor: colorLight, color, fontSize: 14, fontWeight: 800, boxShadow: D.shadow, flexShrink: 0 }}>
      {initials(nombre)}
    </Avatar>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 15, fontWeight: 700, color: D.text, lineHeight: 1.25 }} noWrap>{titleCase(nombre)}</Typography>
      <Typography sx={{ fontSize: 12, color: D.textSoft, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, mt: 0.25 }}>{rol}</Typography>
    </Box>
  </Box>
));

const MetricBadge = memo(({ label, value, unit, bg, color, borderColor }) => (
  <Box sx={{
    display: "flex", flexDirection: "column",
    px: "20px", py: "16px", borderRadius: D.radiusSm,
    bgcolor: bg, border: `1px solid ${borderColor}`,
    width: "100%", minWidth: 0,
  }}>
    <Typography sx={{ fontSize: 11, color: D.textSoft, textTransform: "uppercase", letterSpacing: 0.7, mb: 0.5, fontWeight: 700 }}>{label}</Typography>
    <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.6 }}>
      <Typography sx={{ fontSize: 24, fontWeight: 800, color, lineHeight: 1 }}>{value}</Typography>
      {unit && <Typography sx={{ fontSize: 13, color: D.textSoft, fontWeight: 500 }}>{unit}</Typography>}
    </Box>
  </Box>
));

const AnalisisIACard = memo(({ analisis, loading, error, onRefresh }) => {
  const { t } = useTranslation();
  const parsed = useMemo(
    () => (analisis?.analysisText ? parseAnalysisText(analisis.analysisText) : null),
    [analisis]
  );

  return (
    <Box sx={{
      borderRadius: D.radius,
      border: `1px solid ${D.purpleBorder}`,
      bgcolor: D.surface,
      boxShadow: D.shadow,
      overflow: "hidden",
    }}>
      <Box sx={{ height: 4, bgcolor: D.purple }} />
      <Box sx={{ p: "18px 22px" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5, gap: 1, flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AutoAwesomeIcon sx={{ fontSize: 17, color: D.purple }} />
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: D.text }}>{t('geologia.detail.analysis.title')}</Typography>
          </Box>
          <Button
            onClick={onRefresh}
            disabled={loading}
            size="small"
            startIcon={<RefreshIcon sx={{ fontSize: 15, ...(loading ? { animation: "spin 1s linear infinite" } : {}) }} />}
            sx={{
              fontSize: 11.5, fontWeight: 700, textTransform: "none",
              color: D.purple, minWidth: "auto", px: 1.25,
              "&:hover": { bgcolor: D.purpleLight },
              "@keyframes spin": { "0%": { transform: "rotate(0deg)" }, "100%": { transform: "rotate(360deg)" } },
            }}
          >
            {t('geologia.detail.analysis.refresh')}
          </Button>
        </Box>

        {loading && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, py: 1.25 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.25 }}>
              <Skeleton variant="circular" width={16} height={16} />
              <Skeleton variant="text" width="48%" height={16} />
            </Box>
            <Skeleton variant="rounded" height={84} sx={{ borderRadius: D.radiusXs }} />
            <Skeleton variant="rounded" height={56} sx={{ borderRadius: D.radiusXs }} />
          </Box>
        )}

        {!loading && error && (
          <Box sx={{ p: "10px 14px", borderRadius: D.radiusXs, bgcolor: "#fef2f2", border: "1px solid #fecaca" }}>
            <Typography sx={{ fontSize: 12.5, color: D.red }}>{error}</Typography>
          </Box>
        )}

        {!loading && !error && parsed && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {parsed.sections.map((s) => {
              const isLimitacion = s.title.toLowerCase().includes("limitacion");
              return (
                <Box key={s.title} sx={{
                  ...(isLimitacion && {
                    mt: 1,
                    p: "10px 14px",
                    borderRadius: D.radiusXs,
                    bgcolor: "rgba(0,0,0,0.02)",
                    border: "1px dashed rgba(0,0,0,0.12)",
                  })
                }}>
                  <Typography sx={{
                    fontSize: 10,
                    color: isLimitacion ? D.textSoft : D.purple,
                    textTransform: "uppercase",
                    letterSpacing: 0.7,
                    fontWeight: 700,
                    mb: 0.25
                  }}>
                    {s.title}
                  </Typography>
                  <Typography sx={{
                    fontSize: isLimitacion ? 12.5 : 13,
                    color: isLimitacion ? D.textSoft : D.textMid,
                    lineHeight: 1.65,
                    fontStyle: isLimitacion ? "italic" : "normal"
                  }}>
                    {s.content || "No reportado"}
                  </Typography>
                </Box>
              );
            })}
            {analisis?.generatedAt && (
              <Typography sx={{ fontSize: 10, color: D.textXSoft, mt: 0.5 }}>
                {t('geologia.detail.analysis.generated', {
                  datetime: new Date(analisis.generatedAt).toLocaleString("es-AR", {
                    day: "numeric", month: "numeric", year: "numeric",
                    hour: "2-digit", minute: "2-digit", second: "2-digit",
                    hour12: false,
                  })
                })}
                {analisis.refreshCount > 0
                  ? (analisis.refreshCount === 1
                    ? ` ${t('geologia.detail.analysis.refreshed_one')}`
                    : ` ${t('geologia.detail.analysis.refreshed_other', { count: analisis.refreshCount })}`)
                  : ""}
              </Typography>
            )}
          </Box>
        )}

        {!loading && !error && !parsed && (
          <Typography sx={{ fontSize: 12.5, color: D.textSoft }}>
            {t('geologia.detail.analysis.empty')}
          </Typography>
        )}
      </Box>
    </Box>
  );
});

const usePdfCache = () => {
  const cacheRef = useRef({});
  const getPdfUrl = useCallback((fileName) => cacheRef.current[fileName] || null, []);
  const setPdfUrl = useCallback((fileName, url) => { cacheRef.current[fileName] = url; }, []);
  const clearCache = useCallback(() => { cacheRef.current = {}; }, []);
  return { getPdfUrl, setPdfUrl, clearCache };
};

const PDFPreviewDialog = ({ open, onClose, pdfUrl, fileName, loading }) => {
  const { t } = useTranslation();
  const [scale, setScale] = useState(1.0);
  const [pageWidth, setPageWidth] = useState(null);
  const contentRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const calc = () => {
      if (wrapperRef.current) {
        const available = wrapperRef.current.clientWidth * 0.85;
        setPageWidth(Math.max(400, available));
      }
    };
    setTimeout(calc, 100);
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, [open]);

  useEffect(() => {
    if (open) {
      setScale(1.0);
      setTimeout(() => contentRef.current?.focus(), 100);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleWheel = (e) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setScale((s) => parseFloat(Math.min(3.0, Math.max(0.25, s + delta)).toFixed(2)));
    };
    const handleKeyDown = (e) => {
      if (!e.ctrlKey) return;
      if (e.key === "+" || e.key === "=" || e.key === "-") {
        e.preventDefault();
        e.stopImmediatePropagation();
        setScale((s) =>
          e.key === "-"
            ? parseFloat(Math.max(0.25, s - 0.15).toFixed(2))
            : parseFloat(Math.min(3.0, s + 0.15).toFixed(2))
        );
      }
    };
    document.addEventListener("wheel", handleWheel, { passive: false, capture: true });
    document.addEventListener("keydown", handleKeyDown, { capture: true });
    return () => {
      document.removeEventListener("wheel", handleWheel, { capture: true });
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{ sx: { background: `linear-gradient(160deg, ${D.navy} 0%, #0d1b3e 100%)` } }}
    >
      <Box sx={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        px: 3, py: 1.5,
        background: "rgba(255,255,255,0.04)",
        borderBottom: "1px solid rgba(255,255,255,0.08)",
        backdropFilter: "blur(12px)",
        flexShrink: 0,
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: D.radiusXs,
            background: "rgba(34,211,238,0.12)",
            border: "1px solid rgba(34,211,238,0.25)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <DescriptionIcon sx={{ fontSize: 18, color: D.cyan }} />
          </Box>
          <Box>
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.95rem", letterSpacing: 0.3 }} noWrap>
              {fileName}
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.72rem", letterSpacing: 0.5 }}>
              {t('geologia.pdf.title')}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography sx={{ color: "rgba(255,255,255,0.3)", fontSize: "0.7rem", display: { xs: "none", md: "block" } }}>
            {t('geologia.pdf.shortcut')}
          </Typography>
          <Box sx={{
            display: "flex", alignItems: "center", gap: 0.5,
            background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: D.radiusXs, px: 0.75, py: 0.5,
          }}>
            <IconButton size="small" onClick={() => setScale((s) => parseFloat(Math.max(0.25, s - 0.15).toFixed(2)))}
              sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.08)" }, borderRadius: "6px" }}>
              <ZoomOutIcon fontSize="small" />
            </IconButton>
            <Typography sx={{ color: D.cyan, fontSize: "0.8rem", fontWeight: 700, minWidth: 52, textAlign: "center", fontFamily: "monospace", letterSpacing: 0.5 }}>
              {Math.round(scale * 100)}%
            </Typography>
            <IconButton size="small" onClick={() => setScale((s) => parseFloat(Math.min(3.0, s + 0.15).toFixed(2)))}
              sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.08)" }, borderRadius: "6px" }}>
              <ZoomInIcon fontSize="small" />
            </IconButton>
          </Box>
          <IconButton onClick={onClose} sx={{
            color: "rgba(255,255,255,0.6)", border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: D.radiusXs, width: 36, height: 36,
            "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.08)", borderColor: "rgba(255,255,255,0.25)" },
          }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <DialogContent ref={contentRef} tabIndex={0} sx={{
        display: "flex", justifyContent: "center", alignItems: "flex-start",
        overflowY: "auto", p: { xs: 2, md: 4 }, outline: "none", background: "transparent",
        "&::-webkit-scrollbar": { width: 6 },
        "&::-webkit-scrollbar-track": { background: "rgba(255,255,255,0.03)" },
        "&::-webkit-scrollbar-thumb": { background: "rgba(255,255,255,0.15)", borderRadius: 3 },
      }}>
        <Box ref={wrapperRef} sx={{ width: "100%", display: "flex", justifyContent: "center", alignItems: "flex-start" }}>
          {loading ? (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", pt: 12, gap: 2 }}>
              <Box sx={{ position: "relative", width: 64, height: 64 }}>
                <CircularProgress size={64} thickness={2} sx={{ color: "rgba(255,255,255,0.08)", position: "absolute" }} variant="determinate" value={100} />
                <CircularProgress size={64} thickness={2} sx={{ color: D.cyan, position: "absolute" }} />
              </Box>
              <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.85rem", letterSpacing: 1 }}>
                {t('geologia.pdf.loading')}
              </Typography>
            </Box>
          ) : pdfUrl && pageWidth ? (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
              <Box sx={{ userSelect: "none", borderRadius: D.radius, overflow: "hidden", boxShadow: "0 32px 80px rgba(0,0,0,0.6)" }}>
                <Document
                  file={pdfUrl}
                  loading={<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 8 }}><CircularProgress sx={{ color: D.cyan }} /></Box>}
                >
                  <Page pageNumber={1} width={pageWidth * scale} renderAnnotationLayer={false} renderTextLayer={false} />
                </Document>
              </Box>
            </motion.div>
          ) : null}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const MIN_BLOCK_PX = 80;
const PX_PER_METER = 7.5;

const LithologyColumn = memo(({
  wellDepthRanges, selectedDepth, onSelect,
  onPreview, onAnalizar, analizandoKeys,
}) => {
  const { t } = useTranslation();
  const scrollRef = useRef(null);
  const selectedRef = useRef(null);

  const [consultaMetro, setConsultaMetro] = useState("");
  const [consultaResult, setConsultaResult] = useState(null);
  const [exactDepth, setExactDepth] = useState(null);

  const sorted = useMemo(
    () => [...wellDepthRanges].sort((a, b) => a.prof_inicial - b.prof_inicial),
    [wellDepthRanges]
  );

  useEffect(() => {
    setExactDepth(null);
    setConsultaResult(null);
  }, [sorted]);

  const maxRitmo = useMemo(() => {
    const vals = sorted.map((r) => parseFloat(r.ritmo_mh) || 0);
    return Math.max(...vals, 1);
  }, [sorted]);

  useEffect(() => {
    if (!sorted.length || selectedDepth === null) return;

    const el = selectedRef.current;
    if (!el || !scrollRef.current) return;

    const container = scrollRef.current;
    const rect = el.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const isVisible = rect.top >= containerRect.top && rect.bottom <= containerRect.bottom;

    if (!isVisible) {
      const offset = rect.top - containerRect.top - 24;
      container.scrollTo({ top: container.scrollTop + offset, behavior: "smooth" });
    }
  }, [sorted, selectedDepth]);

  const handleConsulta = useCallback(() => {
    const n = parseFloat(consultaMetro.replace(",", "."));
    if (isNaN(n)) return;
    const found = sorted.find((r) => n >= r.prof_inicial && n <= r.prof_final);
    setConsultaResult(found ? { metro: n, ...found } : { metro: n, notFound: true });
    if (found) {
      onSelect((found.prof_inicial + found.prof_final) / 2);
      setExactDepth(n);
    } else {
      onSelect(null);
      setExactDepth(null);
    }
  }, [consultaMetro, sorted, onSelect]);

  const handleBlockClick = useCallback((range, isSelected) => {
    onSelect(isSelected ? null : (range.prof_inicial + range.prof_final) / 2);
    setExactDepth(null);
  }, [onSelect]);

  if (!sorted.length) return (
    <Box sx={{ p: 4, textAlign: "center" }}>
      <LayersIcon sx={{ fontSize: 32, color: D.textXSoft, mb: 1 }} />
      <Typography sx={{ color: D.textSoft, fontSize: 13 }}>{t('geologia.column.noData')}</Typography>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <Box sx={{
        p: "14px 16px", flexShrink: 0,
        borderBottom: `1px solid ${D.border}`, bgcolor: D.surface,
      }}>
        <Typography sx={{ fontSize: 11, color: D.textSoft, mb: 1.25, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8 }}>
          {t('geologia.column.consultTitle')}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField
            size="small"
            value={consultaMetro}
            onChange={(e) => setConsultaMetro(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleConsulta()}
            placeholder={t('geologia.column.consultPlaceholder')}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: D.radiusXs, height: 36, fontSize: 14, fontWeight: 600,
                bgcolor: D.surfaceAlt,
                "& fieldset": { borderColor: D.border },
                "&:hover fieldset": { borderColor: D.borderMid },
                "&.Mui-focused fieldset": { borderColor: D.blue, borderWidth: "1.5px" },
              },
            }}
            InputProps={{
              endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: 12, color: D.textSoft, fontWeight: 600 }}>m</Typography></InputAdornment>,
            }}
          />
          <Button
            onClick={handleConsulta}
            variant="contained"
            size="small"
            sx={{
              bgcolor: D.blue, color: "#fff", fontWeight: 700, fontSize: 12,
              borderRadius: D.radiusXs, px: 2.5, height: 36, textTransform: "none",
              boxShadow: "none",
              "&:hover": { bgcolor: D.navyMid, boxShadow: "none" },
            }}
          >
            {t('geologia.column.consultButton')}
          </Button>
        </Box>

        {consultaResult && !consultaResult.notFound && (
          <Box sx={{
            mt: 1.5, p: "12px 14px", borderRadius: D.radiusXs,
            bgcolor: D.blueLight, border: `1px solid ${D.blueBorder}`,
          }}>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: D.blue, mb: 0.25 }}>
              {consultaResult.metro?.toFixed(2)} m
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: D.text }}>
              {consultaResult.litologia || t('geologia.column.noLithology')}
            </Typography>
            {consultaResult.color_roca && (
              <Typography sx={{ fontSize: 11, color: D.textSoft, mt: 0.25 }}>{consultaResult.color_roca}</Typography>
            )}
            {consultaResult.alteracion && (() => {
              const ac = ALTER_COLOR(consultaResult.alteracion);
              return (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.75 }}>
                  <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: ac.dot }} />
                  <Typography sx={{ fontSize: 11, color: ac.text, fontWeight: 600 }}>{consultaResult.alteracion}</Typography>
                </Box>
              );
            })()}
            <Box sx={{ display: "flex", gap: 2.5, mt: 0.75, flexWrap: "wrap" }}>
              {consultaResult.ritmo_mh && (
                <Typography sx={{ fontSize: 11, color: D.textSoft }}>
                  Ritmo <strong style={{ color: D.text }}>{consultaResult.ritmo_mh} m/h</strong>
                </Typography>
              )}
              {consultaResult.competencia && (
                <Typography sx={{ fontSize: 11, color: D.textSoft }}>
                  Competencia <strong style={{ color: D.orange }}>{consultaResult.competencia}</strong>
                </Typography>
              )}
            </Box>
            {consultaResult.fecha && (
              <Typography sx={{ fontSize: 10, color: D.textSoft, mt: 0.5 }}>
                {t('geologia.column.partOf', { date: consultaResult.fecha })}
                <span
                  style={{ color: D.blue, cursor: "pointer", marginLeft: 4 }}
                  onClick={() => onSelect((consultaResult.prof_inicial + consultaResult.prof_final) / 2)}
                >
                  · {t('geologia.column.viewDetail')}
                </span>
              </Typography>
            )}
          </Box>
        )}
        {consultaResult?.notFound && (
          <Box sx={{ mt: 1.5, p: "10px 14px", borderRadius: D.radiusXs, bgcolor: D.amberLight, border: `1px solid ${D.amberBorder}` }}>
            <Typography sx={{ fontSize: 12, color: D.amber }}>
              {t('geologia.column.notFound', { depth: consultaResult.metro?.toFixed(2) })}
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${D.border}`,
        bgcolor: D.surface,
        overflow: "hidden",
        mx: { xs: 2, md: 3 },
        mb: { xs: 2, md: 3 },
        mt: 2,
        borderRadius: D.radiusSm,
      }}>

        <Box sx={{
          display: "grid", gridTemplateColumns: "48px 1fr 72px 40px",
          px: 1.5, py: 1.25,
          borderBottom: `1px solid ${D.border}`,
          bgcolor: D.surfaceAlt,
          flexShrink: 0,
        }}>
          {[
            [t('geologia.column.tableHeaders.depth'), "left"],
            [t('geologia.column.tableHeaders.lithoAlter'), "left"],
            [t('geologia.column.tableHeaders.rate'), "right"],
            ["", "center"]
          ].map(([label, align]) => (
            <Typography key={label} sx={{ fontSize: 9.5, fontWeight: 700, color: D.textSoft, textTransform: "uppercase", letterSpacing: 1, textAlign: align }}>
              {label}
            </Typography>
          ))}
        </Box>

        <Box ref={scrollRef} sx={{ flex: 1, overflowY: "auto" }}>
          {sorted.map((range, idx) => {
            const isLast = idx === sorted.length - 1;
            const isSelected = selectedDepth !== null
              && selectedDepth >= range.prof_inicial
              && selectedDepth <= range.prof_final;

            const hasMarker = exactDepth !== null
              && exactDepth >= range.prof_inicial
              && exactDepth <= range.prof_final;
            const markerPct = hasMarker
              ? Math.min(100, Math.max(0,
                ((exactDepth - range.prof_inicial) / (range.prof_final - range.prof_inicial || 1)) * 100
              ))
              : 0;

            const metros = range.prof_final - range.prof_inicial;
            const blockH = Math.max(MIN_BLOCK_PX, metros * PX_PER_METER);
            const lito = LITO_COLOR(range.litologia || "");
            const alt = ALTER_COLOR(range.alteracion || "");
            const ritmo = parseFloat(range.ritmo_mh) || 0;
            const ritmoPct = Math.min(100, (ritmo / maxRitmo) * 100);
            const ritmoColor = ritmo > 2 ? "#16a34a" : ritmo > 1 ? "#65a30d" : "#ea580c";

            const key = rangeKey(range);
            const isPending = !range.litologia;
            const isAnalyzing = analizandoKeys?.has(key);
            const obsTexto = [
              range.obs_geologicas,
              range.observaciones_geologicas,
              range.obs_tecnicas,
              range.observaciones_tecnicas,
              range.observaciones,
            ].map((value) => String(value || "").trim()).find(Boolean) || "";

            return (
              <Box key={`${range.prof_inicial}-${range.prof_final}-${idx}`} sx={{ position: "relative" }}>
                <Box
                  ref={isSelected ? selectedRef : null}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "48px 1fr 72px 40px",
                    minHeight: blockH,
                    borderBottom: `1px solid ${D.border}`,
                    borderLeft: hasMarker
                      ? `3px solid ${D.cyanDark}`
                      : isSelected ? `3px solid ${D.blue}` : "3px solid transparent",
                    bgcolor: hasMarker ? "rgba(34,211,238,0.08)" : isSelected ? D.blueLight : "transparent",
                    transition: "background 0.15s, border-color 0.15s",
                    "&:hover": {
                      bgcolor: hasMarker ? "rgba(34,211,238,0.08)" : isSelected ? D.blueLight : D.surfaceHover,
                    },
                  }}
                >
                  <Box
                    onClick={() => handleBlockClick(range, isSelected)}
                    sx={{
                      display: "flex", flexDirection: "column",
                      justifyContent: "space-between",
                      py: "5px", px: "6px", cursor: "pointer",
                      borderRight: `1px solid ${D.border}`,
                      bgcolor: isSelected ? "#e0eaff" : D.surfaceAlt,
                    }}>
                    <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: isSelected ? D.blue : D.textMid, lineHeight: 1 }}>
                      {range.prof_inicial}
                    </Typography>
                    <Box sx={{ width: "60%", height: "1px", bgcolor: D.border, mx: "auto" }} />
                    <Typography sx={{ fontSize: 9.5, color: D.textSoft, lineHeight: 1 }}>
                      {range.prof_final}
                    </Typography>
                  </Box>

                  {isPending ? (
                    <Box
                      onClick={() => handleBlockClick(range, isSelected)}
                      sx={{
                        position: "relative", cursor: "pointer",
                        p: "10px 12px",
                        display: "flex", flexDirection: "column", justifyContent: "center", gap: 0.75,
                        bgcolor: "rgba(180,83,9,0.03)",
                      }}
                    >
                      <Typography sx={{
                        fontSize: 11.5, color: D.textMid, fontStyle: "italic",
                        lineHeight: 1.5, display: "-webkit-box",
                        WebkitLineClamp: blockH > 100 ? 3 : 2, WebkitBoxOrient: "vertical", overflow: "hidden",
                      }}>
                        {obsTexto ? `“${obsTexto}”` : t('geologia.column.noObservations')}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Box sx={{
                          display: "inline-flex", alignItems: "center", gap: 0.5,
                          px: "8px", py: "2px", borderRadius: "20px",
                          bgcolor: D.amberLight, border: `1px solid ${D.amberBorder}`,
                        }}>
                          <HourglassTopIcon sx={{ fontSize: 11, color: D.amber }} />
                          <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: D.amber }}>{t('geologia.column.pending')}</Typography>
                        </Box>
                        <Box
                          onClick={(e) => { e.stopPropagation(); if (!isAnalyzing) onAnalizar?.(range); }}
                          sx={{
                            display: "inline-flex", alignItems: "center", gap: 0.4,
                            fontSize: 10.5, fontWeight: 700, color: D.purple, cursor: "pointer",
                            "&:hover": { textDecoration: "underline" },
                          }}
                        >
                          {isAnalyzing ? (
                            <>
                              <CircularProgress size={10} thickness={6} sx={{ color: D.purple }} />
                              {t('geologia.column.analyzing')}
                            </>
                          ) : (
                            <>
                              <AutoAwesomeIcon sx={{ fontSize: 12 }} />
                              {t('geologia.column.analyze')}
                            </>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    <Box onClick={() => handleBlockClick(range, isSelected)} sx={{ position: "relative", overflow: "hidden", cursor: "pointer" }}>
                      <Box sx={{
                        position: "absolute", left: 0, top: 0, bottom: 0, width: "62%",
                        bgcolor: lito.fill,
                        opacity: isSelected ? 0.75 : 0.5,
                        transition: "opacity 0.15s",
                      }} />
                      <Box sx={{
                        position: "absolute", left: 0, top: 0, bottom: 0, width: "62%",
                        backgroundImage: `repeating-linear-gradient(135deg, transparent, transparent 4px, ${lito.stripe}44 4px, ${lito.stripe}44 5px)`,
                      }} />
                      {range.alteracion && (
                        <Box sx={{
                          position: "absolute", left: "62%", top: 0, bottom: 0, width: "38%",
                          bgcolor: alt.dot,
                          opacity: isSelected ? 0.30 : 0.18,
                        }} />
                      )}

                      <Box sx={{ position: "relative", zIndex: 1, p: "6px 10px", height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", gap: 0.4 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                          <Typography sx={{ fontSize: 12, fontWeight: 700, color: D.text, lineHeight: 1.3 }}>
                            {range.litologia || "—"}
                          </Typography>
                          {range._iaGenerated && (
                            <Tooltip title={t('geologia.column.tooltipIA')}>
                              <AutoAwesomeIcon sx={{ fontSize: 11, color: D.purple }} />
                            </Tooltip>
                          )}
                        </Box>
                        {range.alteracion && blockH > 52 && (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: alt.dot, flexShrink: 0 }} />
                            <Typography sx={{ fontSize: 10.5, color: D.textMid, lineHeight: 1.2 }}>
                              {range.alteracion}
                            </Typography>
                          </Box>
                        )}
                        {range.color_roca && blockH > 70 && (
                          <Typography sx={{ fontSize: 10, color: D.textSoft, lineHeight: 1.2 }}>
                            {range.color_roca}
                          </Typography>
                        )}
                        {range.competencia && blockH > 80 && (
                          <Box sx={{
                            display: "inline-flex", alignSelf: "flex-start",
                            px: "6px", py: "1px",
                            borderRadius: "4px",
                            bgcolor: D.orangeLight,
                            border: `1px solid ${D.orangeBorder}`,
                            mt: 0.25,
                          }}>
                            <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: D.orange }}>
                              {range.competencia}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}

                  <Box
                    onClick={() => handleBlockClick(range, isSelected)}
                    sx={{
                      display: "flex", alignItems: "center", px: 1.25, cursor: "pointer",
                      borderLeft: `1px solid ${D.border}`,
                    }}>
                    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.75 }}>
                      <Box sx={{ width: "100%", height: 5, bgcolor: D.border, borderRadius: 3, position: "relative", overflow: "hidden" }}>
                        <Box sx={{
                          position: "absolute", left: 0, top: 0, height: "100%",
                          width: `${ritmoPct}%`,
                          bgcolor: ritmoColor,
                          borderRadius: 3,
                          transition: "width 0.3s",
                        }} />
                      </Box>
                      {ritmo > 0 && (
                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: ritmoColor }}>
                          {ritmo.toFixed(2)}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", borderLeft: `1px solid ${D.border}` }}>
                    <Tooltip title={t('geologia.column.tooltipViewPDF')} placement="left">
                      <span>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onPreview?.(range.fileName, range.fecha);
                          }}
                          sx={{
                            width: 26, height: 26, borderRadius: "6px",
                            color: range.fileName ? D.blue : D.textXSoft,
                            "&:hover": { bgcolor: D.blueLight },
                          }}
                        >
                          <VisibilityIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>

                {hasMarker && (
                  <Box sx={{
                    position: "absolute",
                    left: "48px", right: 0,
                    top: `${markerPct}%`,
                    pointerEvents: "none",
                    zIndex: 5,
                  }}>
                    <Box sx={{
                      position: "relative",
                      borderTop: `2px dashed ${D.cyan}`,
                    }}>
                      <Box sx={{
                        position: "absolute",
                        right: 8,
                        top: -13,
                        bgcolor: D.cyan,
                        color: D.cyanText,
                        fontSize: 11,
                        fontWeight: 800,
                        px: "8px",
                        py: "2px",
                        borderRadius: "6px",
                        boxShadow: D.shadowMd,
                        whiteSpace: "nowrap",
                        fontVariantNumeric: "tabular-nums",
                      }}>
                        {exactDepth.toFixed(2)} m
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
});

const ParteDetailSkeleton = memo(({ parte }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
    <Box sx={{
      p: "20px 24px",
      borderRadius: D.radius,
      border: `1px solid ${D.border}`,
      bgcolor: D.surface,
      boxShadow: D.shadow,
    }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Skeleton variant="text" width={parte ? "72%" : "58%"} height={30} sx={{ mb: 0.75 }} />
          <Skeleton variant="text" width={parte ? "40%" : "32%"} height={18} />
        </Box>
        <Skeleton variant="rounded" width={140} height={38} sx={{ borderRadius: D.radiusXs }} />
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 1.25 }}>
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rounded" height={88} sx={{ borderRadius: D.radiusSm }} />
        ))}
      </Box>
    </Box>

    <Box sx={{
      borderRadius: D.radius,
      border: `1px solid ${D.border}`,
      bgcolor: D.surface,
      boxShadow: D.shadow,
      p: "18px 22px",
    }}>
      <Skeleton variant="text" width="42%" height={18} sx={{ mb: 1.5 }} />
      <Skeleton variant="rounded" height={92} sx={{ borderRadius: D.radiusSm, mb: 1 }} />
      <Skeleton variant="rounded" height={64} sx={{ borderRadius: D.radiusSm }} />
    </Box>

    <Box sx={{
      borderRadius: D.radius,
      border: `1px solid ${D.border}`,
      bgcolor: D.surface,
      boxShadow: D.shadow,
      p: "18px 22px",
    }}>
      <Skeleton variant="text" width="36%" height={18} sx={{ mb: 1.25 }} />
      <Skeleton variant="rounded" height={88} sx={{ borderRadius: D.radiusSm }} />
    </Box>
  </Box>
));

const ParteDetail = memo(({ parte, loading, analisisIA, loadingAnalisis, analisisError, onRefreshAnalisis, onPreview }) => {
  const { t } = useTranslation();

  if (loading) return <ParteDetailSkeleton parte={parte} />;

  if (!parte) return (
    <Box sx={{
      display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "center", minHeight: 320, gap: 1.5,
    }}>
      <Box sx={{
        width: 52, height: 52, borderRadius: "50%",
        bgcolor: D.surfaceAlt, border: `1px solid ${D.border}`,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <LayersIcon sx={{ fontSize: 22, color: D.textXSoft }} />
      </Box>
      <Typography sx={{ color: D.textSoft, fontWeight: 600, fontSize: 14 }}>
        {t('geologia.detail.emptyTitle')}
      </Typography>
      <Typography sx={{ color: D.textXSoft, fontSize: 12 }}>
        {t('geologia.detail.emptySub')}
      </Typography>
    </Box>
  );

  const lito = LITO_COLOR(parte.litologia || "");
  const alt = ALTER_COLOR(parte.alteracion || "");
  const ritmo = parseFloat(parte.ritmo_mh) || null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>

      <Box sx={{
        p: "20px 24px",
        borderRadius: D.radius,
        border: `1px solid ${D.border}`,
        bgcolor: D.surface,
        boxShadow: D.shadow,
      }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
          <Box>
            <Typography sx={{ fontSize: 22, fontWeight: 900, color: D.text, lineHeight: 1.1 }}>
              {t('geologia.detail.tramo')} · {parte.prof_inicial} – {parte.prof_final} m
            </Typography>
            <Typography sx={{ fontSize: 13, color: D.textSoft, mt: 0.5, fontWeight: 500 }}>
              {parte.fecha ? formatFecha(parte.fecha) : ""}
            </Typography>
          </Box>
          {parte.fileName && (
            <Button
              onClick={() => onPreview?.(parte.fileName, parte.fecha)}
              startIcon={<DescriptionIcon sx={{ fontSize: 16 }} />}
              size="small"
              sx={{
                fontSize: 12.5, fontWeight: 700, textTransform: "none",
                color: D.blue, bgcolor: D.blueLight, border: `1px solid ${D.blueBorder}`,
                borderRadius: D.radiusXs, px: 1.75, py: 0.75,
                "&:hover": { bgcolor: "#dbeafe" },
              }}
            >
              {t('geologia.detail.viewParte')}
            </Button>
          )}
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 1.25 }}>
          {parte.metros_perf && (
            <MetricBadge
              label={t('geologia.detail.metrics.advance')}
              value={parte.metros_perf}
              unit="m"
              bg={D.blueLight} color={D.blue} borderColor={D.blueBorder}
            />
          )}
          {parte.hs_perf && (
            <MetricBadge
              label={t('geologia.detail.metrics.drilling')}
              value={parte.hs_perf?.slice(0, 5)}
              unit="h"
              bg={D.purpleLight} color={D.purple} borderColor={D.purpleBorder}
            />
          )}
          {ritmo && (
            <MetricBadge
              label={t('geologia.detail.metrics.rate')}
              value={`${ritmo.toFixed(2)}`}
              unit="m/h"
              bg={D.greenLight} color={D.green} borderColor={D.greenBorder}
            />
          )}
          {parte.turno && (() => {
            const raw = parte.turno?.toLowerCase();
            const isDia = raw === "dia";
            const translatedTurno = t(`geologia.turno.${raw}`, { defaultValue: titleCase(parte.turno) });
            return (
              <MetricBadge
                label={t('geologia.detail.metrics.shift')}
                value={translatedTurno}
                bg={isDia ? "#fffbeb" : D.purpleLight}
                color={isDia ? D.amber : D.purple}
                borderColor={isDia ? D.amberBorder : D.purpleBorder}
              />
            );
          })()}
        </Box>
      </Box>

      <AnalisisIACard
        analisis={analisisIA}
        loading={loadingAnalisis}
        error={analisisError}
        onRefresh={onRefreshAnalisis}
      />

      {(parte.litologia || parte.color_roca || parte.mineralogia || parte.estructura || parte.alteracion) && (
        <Box sx={{
          borderRadius: D.radius,
          border: `1px solid ${D.border}`,
          bgcolor: D.surface,
          overflow: "hidden",
          boxShadow: D.shadow,
        }}>
          <Box sx={{ height: 5, background: `linear-gradient(90deg, ${lito.fill} 62%, ${alt.dot} 62%)` }} />

          <Box sx={{ p: "16px 20px" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.75 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: "3px", bgcolor: lito.fill, border: `1px solid ${lito.stripe}` }} />
              <Typography sx={{ fontSize: 16, fontWeight: 800, color: D.text }}>
                {parte.litologia || t('geologia.detail.lithology.title')}
              </Typography>
              {parte.alteracion && (
                <Box sx={{
                  display: "inline-flex", alignItems: "center", gap: 0.5,
                  px: "10px", py: "3px", borderRadius: "20px",
                  bgcolor: alt.bg, border: `1px solid ${alt.border}`,
                }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: alt.dot }} />
                  <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: alt.text }}>
                    {parte.alteracion}
                  </Typography>
                </Box>
              )}
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "14px 28px" }}>
              {[
                [t('geologia.detail.lithology.color'), parte.color_roca],
                [t('geologia.detail.lithology.mineralogy'), parte.mineralogia],
                [t('geologia.detail.lithology.structure'), parte.estructura],
                [t('geologia.detail.lithology.alteration'), parte.alteracion],
              ].filter(([, v]) => v).map(([label, value]) => (
                <Box key={label}>
                  <Typography sx={{ fontSize: 10, color: D.textSoft, textTransform: "uppercase", letterSpacing: 0.8, mb: 0.3, fontWeight: 600 }}>{label}</Typography>
                  <Typography sx={{ fontSize: 13, color: D.text, lineHeight: 1.55 }}>{value}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}

      {parte.obs_geologicas && (
        <Box sx={{
          p: "18px 22px",
          borderRadius: D.radius,
          border: `1px solid ${D.border}`,
          bgcolor: D.surface,
          boxShadow: D.shadow,
        }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.25 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={{ fontSize: 16, lineHeight: 1 }}>💬</Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: D.text }}>{t('geologia.detail.description.title')}</Typography>
            </Box>
            <Typography sx={{ fontSize: 10, color: D.textXSoft, fontStyle: "italic" }}>{t('geologia.detail.description.subtitle')}</Typography>
          </Box>
          <Typography sx={{ fontSize: 13.5, lineHeight: 1.8, color: D.textMid, fontStyle: "italic" }}>
            "{parte.obs_geologicas}"
          </Typography>

          {parte.geologo && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2, pt: 1.5, borderTop: `1px dashed ${D.border}` }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontSize: 12, color: D.textSoft, fontStyle: "italic" }}>
                  {t('geologia.detail.description.signedBy')}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Avatar sx={{ width: 22, height: 22, bgcolor: D.purpleLight, color: D.purple, fontSize: 10, fontWeight: 700 }}>
                    {initials(parte.geologo)}
                  </Avatar>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: D.text }}>
                    {titleCase(parte.geologo)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {parte.actividades?.length > 0 && (
        <Box sx={{
          p: "18px 22px",
          borderRadius: D.radius,
          border: `1px solid ${D.border}`,
          bgcolor: D.surface,
          boxShadow: D.shadow,
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.75 }}>
            <AccessTimeIcon sx={{ color: D.blue, fontSize: 16 }} />
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: D.text }}>
              {t('geologia.detail.activities.title')}
            </Typography>
            <Box sx={{ ml: 0.5, px: "10px", py: "3px", borderRadius: "12px", bgcolor: D.blueLight, border: `1px solid ${D.blueBorder}` }}>
              <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: D.blue, letterSpacing: 0.5 }}>
                {parte.hor_inicial?.slice(0, 5)} – {parte.hor_final?.slice(0, 5)}
              </Typography>
            </Box>
          </Box>
          {parte.actividades.map((a, i) => <ActivityBar key={i} {...a} />)}
        </Box>
      )}

      <Box sx={{
        p: "18px 20px",
        borderRadius: D.radius,
        border: `1px solid ${D.border}`,
        bgcolor: D.surface,
        boxShadow: D.shadow,
      }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: D.textSoft, textTransform: "uppercase", letterSpacing: 0.8, mb: 1.75 }}>
          {t('geologia.detail.team.title')}
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 1.25 }}>
          {parte.geologo && <TeamMember rol={t('geologia.detail.team.geologist')} nombre={parte.geologo} color={D.purple} colorLight={D.purpleLight} />}
          {parte.perforista && <TeamMember rol={t('geologia.detail.team.driller')} nombre={parte.perforista} color={D.blue} colorLight={D.blueLight} />}
          {parte.supervisor && <TeamMember rol={t('geologia.detail.team.supervisor')} nombre={parte.supervisor} color={D.green} colorLight={D.greenLight} />}
        </Box>
      </Box>

    </Box>
  );
});

const GeologiaTab = () => {
  const { t } = useTranslation();
  const { wellId } = useParams();

  const [wells, setWells] = useState([]);
  const [loadingWells, setLoadingWells] = useState(true);
  const [parte, setParte] = useState(null);
  const [loadingParte, setLoadingParte] = useState(true);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [wellDepthRanges, setWellDepthRanges] = useState([]);
  const [pozoSeleccionado, setPozoSeleccionado] = useState(wellId || null);
  const [selectedDepth, setSelectedDepth] = useState(null);

  const [analisisIA, setAnalisisIA] = useState(null);
  const [loadingAnalisis, setLoadingAnalisis] = useState(false);
  const [analisisError, setAnalisisError] = useState(null);

  const [analizandoKeys, setAnalizandoKeys] = useState(new Set());

  const pdfCache = usePdfCache();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewFileName, setPreviewFileName] = useState("");
  const detalleRequestRef = useRef(0);

  const headerStats = useMemo(() => {
    if (!wellDepthRanges.length) return null;
    const profActual = Math.max(...wellDepthRanges.map((r) => r.prof_final));
    const tramos = wellDepthRanges.length;
    const litologias = [...new Set(wellDepthRanges.map((r) => r.litologia).filter(Boolean))];
    const alteraciones = [...new Set(wellDepthRanges.map((r) => r.alteracion).filter(Boolean))];
    const fechas = [...new Set(wellDepthRanges.map((r) => r.fecha).filter(Boolean))];
    fechas.sort((a, b) => parseFechaToTimestamp(a) - parseFechaToTimestamp(b));
    return { profActual, tramos, litologias, alteraciones, fechas };
  }, [wellDepthRanges]);

  useEffect(() => {
    let cancelled = false;
    const fetch_ = async () => {
      setLoadingWells(true);
      setLoadingInitial(true);
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/geology/wells`, { withCredentials: true });
        if (cancelled) return;
        setWells(res.data.wells || []);
        const nextWell = (() => {
          const names = (res.data.wells || []).map((w) => w.name);
          if (wellId && names.includes(wellId)) return wellId;
          return names[0] || null;
        })();
        setPozoSeleccionado(nextWell);
        if (!nextWell) {
          setWellDepthRanges([]);
          setParte(null);
          setSelectedDepth(null);
          setAnalisisIA(null);
          setAnalisisError(null);
        }
      } catch (err) { console.error(err); }
      finally { if (!cancelled) setLoadingWells(false); }
    };
    fetch_();
    return () => { cancelled = true; };
  }, [wellId]);

  const aplicarAnalisisARango = useCallback((parteRef, analisis) => {
    if (!parteRef || !analisis?.analysisText) return;
    const { sections } = parseAnalysisText(analisis.analysisText);
    const litoSection = sections.find((s) => s.title === "Litología");
    const altSection = sections.find((s) => s.title === "Alteraciones");
    if (!litoSection?.content) return;

    setWellDepthRanges((prev) => prev.map((r) => {
      const mismoRango = r.prof_inicial === parteRef.prof_inicial && r.prof_final === parteRef.prof_final;
      if (!mismoRango || r.litologia) return r;
      return {
        ...r,
        litologia: litoSection.content,
        alteracion: r.alteracion || altSection?.content || "",
        _iaGenerated: true,
      };
    }));
  }, []);

  const cargarDetalle = useCallback(async (pozo, depth) => {
    if (!pozo) {
      setLoadingParte(false);
      setLoadingInitial(false);
      return;
    }

    const requestId = ++detalleRequestRef.current;
    setLoadingParte(true);
    setAnalisisError(null);

    try {
      const params = { pozo };
      if (depth !== null && depth !== undefined) params.depth = depth;

      const resParte = await axios.get(`${process.env.REACT_APP_API_URL}/api/geology/parte`, { params, withCredentials: true });
      if (requestId !== detalleRequestRef.current) return;

      const p = resParte.data.parte || null;
      setParte(p);

      if (p?._id) {
        setLoadingAnalisis(true);
        try {
          const resAnalisis = await axios.get(`${process.env.REACT_APP_API_URL}/api/geology/analisis`, { params, withCredentials: true });
          if (requestId !== detalleRequestRef.current) return;

          const nuevoAnalisis = resAnalisis.data.analisis || null;
          setAnalisisIA(nuevoAnalisis);
          if (nuevoAnalisis) aplicarAnalisisARango(p, nuevoAnalisis);
        } catch (err) {
          if (requestId !== detalleRequestRef.current) return;
          setAnalisisIA(null);
          setAnalisisError(err.response?.data?.error || "No se pudo generar el análisis geológico.");
          console.error(err);
        } finally {
          if (requestId === detalleRequestRef.current) setLoadingAnalisis(false);
        }
      } else {
        if (requestId === detalleRequestRef.current) setAnalisisIA(null);
      }
    } catch (err) {
      if (requestId !== detalleRequestRef.current) return;
      if (err.response?.status === 404) setParte(null);
      else console.error(err);
    } finally {
      if (requestId === detalleRequestRef.current) {
        setLoadingParte(false);
        setLoadingInitial(false);
      }
    }
  }, [aplicarAnalisisARango]);

  const handleDepthSelect = useCallback((d) => {
    setSelectedDepth(d);
    cargarDetalle(pozoSeleccionado, d);
  }, [cargarDetalle, pozoSeleccionado]);

  useEffect(() => {
    if (!pozoSeleccionado) return;
    let cancelled = false;

    setLoadingParte(true);
    setLoadingInitial(true);

    const loadRanges = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/geology/ranges`, {
          params: { pozo: pozoSeleccionado }, withCredentials: true,
        });
        if (cancelled) return;

        const ranges = res.data.ranges || [];
        setWellDepthRanges(ranges);

        if (!ranges.length) {
          setParte(null);
          setSelectedDepth(null);
          setAnalisisIA(null);
          setLoadingParte(false);
          setLoadingInitial(false);
          return;
        }

        const sorted = [...ranges].sort((a, b) => a.prof_inicial - b.prof_inicial);
        const last = sorted[sorted.length - 1];
        const depth = (last.prof_inicial + last.prof_final) / 2;

        if (cancelled) return;
        setSelectedDepth(depth);
        await cargarDetalle(pozoSeleccionado, depth);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setLoadingParte(false);
          setLoadingInitial(false);
        }
      }
    };
    loadRanges();
    return () => { cancelled = true; };
  }, [pozoSeleccionado]);

  const handleRefreshAnalisis = useCallback(async () => {
    if (!pozoSeleccionado || !parte?._id) return;
    setLoadingAnalisis(true);
    setAnalisisError(null);
    try {
      const params = { pozo: pozoSeleccionado, refresh: "true" };
      if (selectedDepth !== null && selectedDepth !== undefined) params.depth = selectedDepth;
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/geology/analisis`, { params, withCredentials: true });
      const nuevoAnalisis = res.data.analisis || null;
      setAnalisisIA(nuevoAnalisis);
      if (nuevoAnalisis) aplicarAnalisisARango(parte, nuevoAnalisis);
    } catch (err) {
      setAnalisisError(err.response?.data?.error || "No se pudo refrescar el análisis geológico.");
      console.error(err);
    } finally {
      setLoadingAnalisis(false);
    }
  }, [pozoSeleccionado, selectedDepth, parte?._id, parte, aplicarAnalisisARango]);

  const handleAnalizarRango = useCallback(async (range) => {
    if (!pozoSeleccionado) return;
    const key = rangeKey(range);
    setAnalizandoKeys((prev) => new Set(prev).add(key));
    try {
      const depth = (range.prof_inicial + range.prof_final) / 2;
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/geology/analisis`, {
        params: { pozo: pozoSeleccionado, depth }, withCredentials: true,
      });
      const nuevoAnalisis = res.data.analisis || null;
      if (nuevoAnalisis) {
        aplicarAnalisisARango(range, nuevoAnalisis);
      }

      setSelectedDepth(depth);
      await cargarDetalle(pozoSeleccionado, depth);
    } catch (err) {
      console.error(err);
    } finally {
      setAnalizandoKeys((prev) => { const n = new Set(prev); n.delete(key); return n; });
    }
  }, [pozoSeleccionado, aplicarAnalisisARango, cargarDetalle]);

  const handlePreview = useCallback(async (fileName, fecha) => {
    if (!fileName) {
      console.warn("No hay fileName para abrir la previsualización");
      return;
    }

    setPreviewFileName(fileName);
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewUrl(null);

    const cached = pdfCache.getPdfUrl(fileName);
    if (cached) {
      setPreviewUrl(cached);
      setPreviewLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token");
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/get-pdf-url`, {
        responseType: "blob",
        withCredentials: true,
        params: { fileName, pozo: pozoSeleccionado },
        timeout: 20000,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      pdfCache.setPdfUrl(fileName, url);
      setPreviewUrl(url);
    } catch (err) {
      console.error("Error al abrir el PDF del parte diario", err);
      setPreviewUrl(null);
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  }, [pdfCache, pozoSeleccionado]);

  useEffect(() => () => pdfCache.clearCache(), [pdfCache]);

  if (loadingWells || loadingInitial) return (
    <Box sx={{ minHeight: "100vh", bgcolor: D.canvas }}>
      <Box sx={{ bgcolor: D.navy, px: { xs: 2, md: 4 }, pt: 3, pb: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5, flexWrap: "wrap" }}>
          <Skeleton variant="rounded" width={110} height={34} sx={{ bgcolor: "rgba(255,255,255,0.12)" }} />
          <Skeleton variant="rounded" width={180} height={40} sx={{ bgcolor: "rgba(255,255,255,0.12)" }} />
        </Box>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" width={180} height={92} sx={{ bgcolor: "rgba(255,255,255,0.12)" }} />
          ))}
        </Box>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "420px 1fr" }, alignItems: "stretch" }}>
        <Box sx={{ bgcolor: D.surface, minHeight: 560, p: 3 }}>
          <Skeleton variant="text" width="60%" height={24} sx={{ mb: 2 }} />
          <Skeleton variant="rounded" height={80} sx={{ mb: 2, borderRadius: D.radiusSm }} />
          <Skeleton variant="rounded" height={360} sx={{ borderRadius: D.radiusSm }} />
        </Box>
        <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: D.canvas }}>
          <ParteDetailSkeleton parte={null} />
        </Box>
      </Box>
    </Box>
  );

  const currentWell = wells.find((w) => w.name === pozoSeleccionado);
  const columnaCargando = loadingParte && wellDepthRanges.length > 0;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: D.canvas }}>

      <Box sx={{ px: { xs: 2, md: 4 }, pt: 3, pb: 2.5 }}>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5, flexWrap: "wrap" }}>
          <Box sx={{
            display: "inline-flex", alignItems: "center", gap: 0.75,
            px: "10px", py: "5px",
            borderRadius: "6px",
            bgcolor: D.surface,
            border: `1px solid ${D.borderMid}`,
            boxShadow: D.shadow,
          }}>
            <TerrainIcon sx={{ fontSize: 16, color: D.blue }} />
            <Typography sx={{ fontSize: 11, fontWeight: 800, color: D.navyMid, letterSpacing: 1.2, textTransform: "uppercase" }}>
              {t('geologia.header.wellLabel')}
            </Typography>
          </Box>

          <FormControl sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px", fontSize: "1.15rem", fontWeight: 800,
              height: 42, color: D.text,
              bgcolor: D.surface,
              boxShadow: D.shadow,
              "& fieldset": { borderColor: D.borderMid },
              "&:hover fieldset": { borderColor: D.textSoft },
              "&.Mui-focused fieldset": { borderColor: D.blue, borderWidth: 2 },
              "& .MuiSvgIcon-root": { color: D.textMid },
            },
            "& .MuiSelect-select": { py: "8px", px: 2 },
          }}>
            <Select
              value={pozoSeleccionado || ""}
              onChange={(e) => setPozoSeleccionado(e.target.value)}
              sx={{ color: D.text, minWidth: 160 }}
              MenuProps={{ PaperProps: { sx: { borderRadius: D.radiusSm, mt: 0.5, boxShadow: D.shadowLg } } }}
            >
              {wells.map((w) => (
                <MenuItem key={w.name} value={w.name} sx={{ fontSize: "1rem", fontWeight: 700 }}>
                  {w.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {currentWell?.proyecto && (
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: D.textMid, letterSpacing: 0.2, bgcolor: "rgba(0,0,0,0.04)", px: 1.5, py: 0.5, borderRadius: 2 }}>
              {currentWell.proyecto}
            </Typography>
          )}

          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1.5, bgcolor: D.surface, px: 2, py: 1, borderRadius: "8px", border: `1px solid ${D.borderMid}`, boxShadow: D.shadow }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#16a34a", boxShadow: "0 0 0 3px rgba(22, 163, 74, 0.2)" }} />
            {headerStats?.fechas?.length > 0 && (
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: D.textMid }}>
                {t('geologia.header.lastPart')} <strong style={{ color: D.text }}>{headerStats.fechas[headerStats.fechas.length - 1]}</strong>
              </Typography>
            )}
          </Box>
        </Box>

        {headerStats && (
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <HeaderStat
              label={t('geologia.header.depthTitle')}
              value={headerStats.profActual?.toLocaleString("es-AR", { minimumFractionDigits: 2 })}
              unit="m"
              sub={t('geologia.header.depthSub', { name: pozoSeleccionado })}
            />
            <HeaderStat
              label={t('geologia.header.sectionsTitle')}
              value={headerStats.tramos}
              sub={
                headerStats.fechas.length > 1
                  ? t('geologia.header.sectionsSubRange', {
                    startDate: headerStats.fechas[0],
                    endDate: headerStats.fechas[headerStats.fechas.length - 1]
                  })
                  : headerStats.fechas.length === 1
                    ? t('geologia.header.sectionsSubSingle', { date: headerStats.fechas[0] })
                    : ""
              }
            />
            <HeaderStat
              label={t('geologia.header.lithologiesTitle')}
              value={headerStats.litologias.length}
              sub={headerStats.litologias.slice(0, 3).join(" · ")}
            />
            <HeaderStat
              label={t('geologia.header.alterationsTitle')}
              value={headerStats.alteraciones.length}
              sub={headerStats.alteraciones.slice(0, 3).join(" · ")}
            />
          </Box>
        )}
      </Box>

      <Box sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", lg: "420px 1fr" },
        alignItems: "stretch",
      }}>

        <Box sx={{ position: "relative", height: { xs: 600, lg: "auto" } }}>
          <Box sx={{
            position: { lg: "absolute" },
            top: 0, left: 0, right: 0, bottom: 0,
            borderRight: { lg: `1px solid ${D.border}` },
            bgcolor: D.surface,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            height: { xs: "100%", lg: "auto" },
          }}>
            <Box sx={{ px: { xs: 2, md: 3 }, pt: { xs: 2, md: 3 }, pb: 1.5, flexShrink: 0, borderBottom: `1px solid ${D.border}` }}>
              <Typography sx={{ fontSize: 14, fontWeight: 800, color: D.text }}>{t('geologia.column.title')}</Typography>
              <Typography sx={{ fontSize: 12, color: D.textSoft, mt: 0.25 }}>
                {t('geologia.column.subtitle')}
              </Typography>
            </Box>

            {columnaCargando && (
              <LinearProgress sx={{
                height: 2, flexShrink: 0,
                bgcolor: "transparent",
                "& .MuiLinearProgress-bar": { bgcolor: D.blue },
              }} />
            )}

            <Box sx={{
              flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column",
              opacity: columnaCargando ? 0.55 : 1,
              pointerEvents: columnaCargando ? "none" : "auto",
              transition: "opacity 0.15s",
            }}>
              <LithologyColumn
                wellDepthRanges={wellDepthRanges}
                selectedDepth={selectedDepth}
                onSelect={handleDepthSelect}
                onPreview={handlePreview}
                onAnalizar={handleAnalizarRango}
                analizandoKeys={analizandoKeys}
              />
            </Box>
          </Box>
        </Box>

        <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: D.canvas }}>
          <ParteDetail
            parte={parte}
            loading={loadingParte && !parte}
            analisisIA={analisisIA}
            loadingAnalisis={loadingAnalisis}
            analisisError={analisisError}
            onRefreshAnalisis={handleRefreshAnalisis}
            onPreview={handlePreview}
          />
        </Box>
      </Box>

      <PDFPreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        pdfUrl={previewUrl}
        fileName={previewFileName}
        loading={previewLoading}
      />

    </Box>
  );
};

export default GeologiaTab;